import React, { useState, useEffect } from 'react';
import clientDB from '../config/clientDatabase';
import './UserManagement.css';

const UserManagement = ({ user, company }) => {
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [filter, setFilter] = useState('all'); // 'all', 'pending', 'approved', 'rejected'
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = () => {
    setLoading(true);
    try {
      const allUsers = clientDB.getAllUsers();
      const pending = clientDB.getPendingUsers();

      // Sort users by date joined (newest first)
      allUsers.sort((a, b) => {
        const dateA = new Date(a.joinedDate || '2024-01-01');
        const dateB = new Date(b.joinedDate || '2024-01-01');
        return dateB - dateA;
      });

      setUsers(allUsers);
      setPendingUsers(pending);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveUser = (email) => {
    if (window.confirm(`Approve user ${email}?`)) {
      if (clientDB.approveUser(email)) {
        alert(`User ${email} has been approved and can now access the portal.`);
        loadUsers();
      } else {
        alert('Error approving user. Please try again.');
      }
    }
  };

  const handleRejectUser = (email) => {
    if (window.confirm(`Reject user ${email}? This will prevent them from accessing the portal.`)) {
      if (clientDB.rejectUser(email)) {
        alert(`User ${email} has been rejected.`);
        loadUsers();
      } else {
        alert('Error rejecting user. Please try again.');
      }
    }
  };

  const handleChangeRole = (email, newRole) => {
    if (window.confirm(`Change role for ${email} to ${newRole}?`)) {
      if (clientDB.updateUserRole(email, newRole)) {
        alert(`Role updated successfully.`);
        loadUsers();
      } else {
        alert('Error updating role. Please try again.');
      }
    }
  };

  const handleRemoveUser = (email) => {
    if (window.confirm(`Remove user ${email}? This will deactivate their account.`)) {
      if (clientDB.removeUser(email)) {
        alert(`User ${email} has been removed.`);
        loadUsers();
      } else {
        alert('Error removing user. Please try again.');
      }
    }
  };

  const getFilteredUsers = () => {
    let filtered = users;

    // Apply status filter
    if (filter === 'pending') {
      filtered = filtered.filter(u => u.approvalStatus === 'pending');
    } else if (filter === 'approved') {
      filtered = filtered.filter(u => u.approvalStatus === 'approved');
    } else if (filter === 'rejected') {
      filtered = filtered.filter(u => u.approvalStatus === 'rejected');
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(u =>
        u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.companyId?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  };

  const getStatusBadge = (user) => {
    if (user.approvalStatus === 'pending') {
      return <span className="badge badge-warning">Pending Approval</span>;
    } else if (user.approvalStatus === 'approved') {
      return <span className="badge badge-success">Approved</span>;
    } else if (user.approvalStatus === 'rejected') {
      return <span className="badge badge-danger">Rejected</span>;
    }
    return <span className="badge badge-secondary">Unknown</span>;
  };

  const getRoleBadge = (role) => {
    const roleColors = {
      'owner': 'badge-purple',
      'admin': 'badge-blue',
      'manager': 'badge-green',
      'viewer': 'badge-gray'
    };
    return <span className={`badge ${roleColors[role] || 'badge-gray'}`}>{role}</span>;
  };

  const filteredUsers = getFilteredUsers();

  return (
    <div className="user-management">
      <div className="component-header">
        <h2>User Management</h2>
        {pendingUsers.length > 0 && (
          <div className="pending-alert">
            <span className="alert-icon">⚠️</span>
            <span>{pendingUsers.length} user{pendingUsers.length !== 1 ? 's' : ''} pending approval</span>
          </div>
        )}
      </div>

      <div className="management-controls">
        <div className="filter-controls">
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All Users ({users.length})
          </button>
          <button
            className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
            onClick={() => setFilter('pending')}
          >
            Pending ({pendingUsers.length})
          </button>
          <button
            className={`filter-btn ${filter === 'approved' ? 'active' : ''}`}
            onClick={() => setFilter('approved')}
          >
            Approved ({users.filter(u => u.approvalStatus === 'approved').length})
          </button>
          <button
            className={`filter-btn ${filter === 'rejected' ? 'active' : ''}`}
            onClick={() => setFilter('rejected')}
          >
            Rejected ({users.filter(u => u.approvalStatus === 'rejected').length})
          </button>
        </div>

        <div className="search-box">
          <input
            type="text"
            placeholder="Search by email, name, or company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading users...</p>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="empty-state">
          <p>No users found matching your criteria.</p>
        </div>
      ) : (
        <div className="users-table">
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>Company</th>
                <th>Role</th>
                <th>Status</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => {
                const userCompany = clientDB.getCompany(user.companyId);
                return (
                  <tr key={user.email} className={user.approvalStatus === 'pending' ? 'pending-row' : ''}>
                    <td>
                      <div className="user-info">
                        <div className="user-avatar">
                          {user.name ? user.name.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="user-name">{user.name || 'No Name'}</div>
                          <div className="user-email">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>{userCompany?.name || 'Unknown'}</td>
                    <td>{getRoleBadge(user.role)}</td>
                    <td>{getStatusBadge(user)}</td>
                    <td>{user.joinedDate || 'Unknown'}</td>
                    <td>
                      <div className="action-buttons">
                        {user.approvalStatus === 'pending' && (
                          <>
                            <button
                              className="btn-small btn-approve"
                              onClick={() => handleApproveUser(user.email)}
                              title="Approve User"
                            >
                              ✓ Approve
                            </button>
                            <button
                              className="btn-small btn-reject"
                              onClick={() => handleRejectUser(user.email)}
                              title="Reject User"
                            >
                              ✗ Reject
                            </button>
                          </>
                        )}
                        {user.approvalStatus === 'approved' && user.email !== 'ryan@1209digital.com' && (
                          <>
                            <select
                              className="role-select"
                              value={user.role}
                              onChange={(e) => handleChangeRole(user.email, e.target.value)}
                            >
                              <option value="viewer">Viewer</option>
                              <option value="manager">Manager</option>
                              <option value="admin">Admin</option>
                              {userCompany?.type === 'admin' && <option value="owner">Owner</option>}
                            </select>
                            <button
                              className="btn-small btn-remove"
                              onClick={() => handleRemoveUser(user.email)}
                              title="Remove User"
                            >
                              Remove
                            </button>
                          </>
                        )}
                        {user.approvalStatus === 'rejected' && (
                          <button
                            className="btn-small btn-approve"
                            onClick={() => handleApproveUser(user.email)}
                            title="Reconsider User"
                          >
                            Reconsider
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selectedUser && (
        <div className="user-detail-modal" onClick={() => setSelectedUser(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>User Details</h3>
              <button className="close-btn" onClick={() => setSelectedUser(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-row">
                <label>Email:</label>
                <span>{selectedUser.email}</span>
              </div>
              <div className="detail-row">
                <label>Name:</label>
                <span>{selectedUser.name || 'Not provided'}</span>
              </div>
              <div className="detail-row">
                <label>Company:</label>
                <span>{clientDB.getCompany(selectedUser.companyId)?.name || 'Unknown'}</span>
              </div>
              <div className="detail-row">
                <label>Role:</label>
                <span>{selectedUser.role}</span>
              </div>
              <div className="detail-row">
                <label>Status:</label>
                <span>{selectedUser.approvalStatus}</span>
              </div>
              <div className="detail-row">
                <label>Joined:</label>
                <span>{selectedUser.joinedDate || 'Unknown'}</span>
              </div>
              <div className="detail-row">
                <label>Last Login:</label>
                <span>{selectedUser.lastLogin || 'Never'}</span>
              </div>
              <div className="detail-row">
                <label>Invited By:</label>
                <span>{selectedUser.invitedBy || 'Self-registered'}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;